<!-- @catalog The settled-verdict block of a drawer: icon tile, what the verdict says, its explanation and stamp, plus room for an action menu. -->
<template>
  <section class="c-assessment-result">
    <h4 v-if="title">{{ title }}</h4>

    <div
      class="c-assessment-result__content"
      :class="{ 'c-assessment-result__content--bordered': bordered }"
    >
      <!-- The whole tile, so a caller with its own medal replaces it outright
           rather than colouring ours. -->
      <slot name="icon">
        <span
          class="c-assessment-result__icon"
          :class="`c-assessment-result__icon--${variant}`"
        >
          <q-icon :name="icon ?? DEFAULT_ICONS[variant]" size="20px" />
        </span>
      </slot>

      <div class="c-assessment-result__body">
        <span class="c-assessment-result__label">{{ label }}</span>

        <span v-if="explanation" class="c-assessment-result__meta">
          {{ explanation }}
        </span>

        <span v-if="meta" class="c-assessment-result__meta">{{ meta }}</span>
      </div>

      <slot name="actions" />
    </div>
  </section>
</template>

<script setup lang="ts">
export type AssessmentResultVariant = "positive" | "negative" | "pending";

const DEFAULT_ICONS: Record<AssessmentResultVariant, string> = {
  positive: "fa-solid fa-award",
  negative: "fa-solid fa-xmark",
  pending: "fa-regular fa-clock",
};

withDefaults(
  defineProps<{
    // What the verdict says; the only thing every caller has.
    label: string;
    title?: string | null;
    // The optional words that came with the verdict.
    explanation?: string | null;
    // The line under it: when it was given, and by whom.
    meta?: string | null;
    variant?: AssessmentResultVariant;
    // Overrides the variant's own icon.
    icon?: string | null;
    // Boxes the block in a bordered card.
    bordered?: boolean;
  }>(),
  {
    title: null,
    explanation: null,
    meta: null,
    variant: "pending",
    icon: null,
    bordered: false,
  },
);
</script>

<style scoped lang="scss">
.c-assessment-result {
  @apply tw-flex tw-flex-col tw-gap-4;

  &__content {
    @apply tw-flex tw-items-center tw-gap-4;

    &--bordered {
      @apply tw-rounded-lg tw-border tw-border-gray-200 tw-p-4;
    }
  }

  &__icon {
    @apply tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-p-2.5;

    &--positive {
      @apply tw-bg-primary-100 tw-text-primary;
    }

    &--negative {
      @apply tw-bg-gray-100 tw-text-gray-900;
    }

    &--pending {
      @apply tw-bg-gray-100 tw-text-gray-500;
    }
  }

  &__body {
    @apply tw-flex tw-flex-1 tw-flex-col;
  }

  &__label {
    @apply tw-font-bold tw-text-gray-900;
  }

  &__meta {
    @apply tw-text-sm tw-text-gray-500;
  }
}
</style>
